import { AgDialog, SingleAutocomplete } from '@australiangreens/ag-internal-components';
import {
  Button,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import { adjustPageNo } from '@/util';
import SavedSearchesTable from '@pages/SavedSearchesPage/SavedSearchesTable';
import EditSearchDialog from './EditSearchDialog';
import { useSavedSearchesPage } from './useSavedSearchesPage';

type SavedSearchesTabProps = Pick<
  ReturnType<typeof useSavedSearchesPage>,
  | 'lookupUsers'
  | 'searchName'
  | 'searchType'
  | 'searchDescription'
  | 'searchCreatedBy'
  | 'handleSearchNameChange'
  | 'handleSearchDescriptionChange'
  | 'handleSearchCreatedByChange'
  | 'handleSearchTypeChange'
  | 'handleResetFilter'
  | 'isInitialSearchLoading'
  | 'mySearchesMapped'
  | 'searchPageNoAtomModel'
  | 'searchPageSizeAtomModel'
  | 'setSearchPageNoAtomModel'
  | 'setSearchPageSizeAtomModel'
  | 'searchColumnVisibilityAtomModel'
  | 'setSearchColumnVisibilityAtomModel'
  | 'searchSortAtomModel'
  | 'setSearchSortAtomModel'
  | 'setSearchForDelete'
  | 'setSearchForEdit'
  | 'searchForDelete'
  | 'handleDeleteSearchDialogDelete'
  | 'searchForEdit'
>;

export default function SavedSearchesTab({
  lookupUsers,
  searchName,
  searchType,
  searchDescription,
  searchCreatedBy,
  handleSearchNameChange,
  handleSearchDescriptionChange,
  handleSearchCreatedByChange,
  handleSearchTypeChange,
  handleResetFilter,
  isInitialSearchLoading,
  mySearchesMapped,
  searchPageNoAtomModel,
  searchPageSizeAtomModel,
  setSearchPageNoAtomModel,
  setSearchPageSizeAtomModel,
  searchColumnVisibilityAtomModel,
  setSearchColumnVisibilityAtomModel,
  searchSortAtomModel,
  setSearchSortAtomModel,
  setSearchForDelete,
  setSearchForEdit,
  searchForDelete,
  handleDeleteSearchDialogDelete,
  searchForEdit,
}: SavedSearchesTabProps) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '12px',
          marginBottom: '16px',
          gap: '16px',
        }}
      >
        <div>
          <TextField
            id="search-textfield-name"
            label={'Search name'}
            type="text"
            variant="outlined"
            value={searchName}
            onChange={handleSearchNameChange}
            color="primary"
            sx={{ width: '260px' }}
            autoComplete="off"
          />
        </div>

        <div>
          <FormControl sx={{ width: '120px' }}>
            <InputLabel id="search-select-type-label" color="primary">
              Type
            </InputLabel>
            <Select
              labelId="search-select-type-label"
              id="search-select-type"
              value={searchType}
              label="Type"
              onChange={handleSearchTypeChange}
              color="primary"
            >
              <MenuItem value={''}>&nbsp;</MenuItem>
              <MenuItem value={'Quick'}>Quick</MenuItem>
              <MenuItem value={'Advanced'}>Advanced</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div>
          <SingleAutocomplete
            data-testid="selectCreatedBy"
            onChange={handleSearchCreatedByChange}
            value={searchCreatedBy}
            label={'Created by'}
            lookup={lookupUsers}
            noOptionsText="No users found"
            minLength={3}
            sx={{ width: '220px' }}
            textFieldColor="primary"
            textFieldVariant="outlined"
          />
        </div>

        <div>
          <TextField
            id="search-textfield-description"
            label={'Description'}
            type="text"
            sx={{ width: '220px' }}
            variant="outlined"
            value={searchDescription}
            onChange={handleSearchDescriptionChange}
            color="primary"
            autoComplete="off"
          />
        </div>

        <div>
          <Button
            onClick={handleResetFilter}
            color="primary"
            sx={{
              paddingTop: '6px',
              paddingBottom: '6px',
              marginTop: '12px',
              width: '109px',
            }}
          >
            Reset Filter
          </Button>
        </div>
      </div>

      <SavedSearchesTable
        isLoading={isInitialSearchLoading}
        data={mySearchesMapped ?? []}
        paginationModel={{
          page: searchPageNoAtomModel,
          pageSize: searchPageSizeAtomModel,
        }}
        onPaginationModelChange={(model) => {
          setSearchPageSizeAtomModel(model.pageSize);
          setSearchPageNoAtomModel(
            model.pageSize !== searchPageSizeAtomModel
              ? adjustPageNo(searchPageSizeAtomModel, searchPageNoAtomModel, model.pageSize)
              : model.page
          );
        }}
        columnVisibilityModel={searchColumnVisibilityAtomModel}
        onColumnVisibilityModelChange={(newModel) => {
          setSearchColumnVisibilityAtomModel(newModel);
        }}
        activateDelete={setSearchForDelete}
        activateEdit={setSearchForEdit}
        sortModel={searchSortAtomModel}
        onSortModelChange={(newSortModel) => {
          setSearchSortAtomModel(newSortModel);
        }}
      />
      <AgDialog
        dialogTitle={'Delete search'}
        isOpen={searchForDelete !== undefined}
        onClose={() => setSearchForDelete(undefined)}
        primaryButton={{
          text: 'Delete',
          onClick: handleDeleteSearchDialogDelete,
        }}
      >
        <DialogContentText>
          Are you sure you want to delete the search <strong>{searchForDelete?.name}</strong>?
        </DialogContentText>
      </AgDialog>
      <EditSearchDialog
        currentSearch={
          searchForEdit ?? {
            name: '',
            description: '',
            searchJson: '',
            id: 0,
            searchType: 'Quick',
          }
        }
        handleClose={() => setSearchForEdit(undefined)}
        isOpen={searchForEdit !== undefined}
        key={searchForEdit?.id ?? 'closed'}
      />
    </>
  );
}
